<template>
    <div class="LifekeyPopup">
        <div class="header">
            <Belt v-bind="beltProps" />

            <AnimatedSatellitesProgress
                :prior-progress="priorProgress"
                :progress="progress"
                :satellite-count="satelliteCount"
                :prior-satellite-count="priorSatelliteCount"
                track-color="#2A467E"
                color="#6FEEF6"
                @onSatellitesCollapse="showNewBelt = true"
            >
                <template v-slot:satellite="{ index, progress }">
                    <Lifekey :completed="progress === 1" />
                </template>
            </AnimatedSatellitesProgress>
        </div>
        <div class="body">
            <template v-if="belt.current && belt.points === 0">
                <div class="title">
                    {{ $t('newBelt', { beltName: currentBeltName }) }}
                </div>
                <NextBeltProgressText v-if="belt.next" class="text" :belt="belt" />
            </template>
            <template v-else>
                <div class="title">
                    {{ $t('newLifekey') }}
                </div>
                <NextBeltProgressText v-if="belt.next" class="text" :belt="belt" />
            </template>
            <div class="continueButtonContainer">
                <StandardButton
                    v-if="continueToDashboard"
                    class="continueButton"
                    :label="$t('continueButton')"
                    @click="navigateToDashboard()"
                />
            </div>
        </div>
        <div v-if="rank" class="footer">
            {{ $t('rankText', { rank }) }}
        </div>
    </div>
</template>

<translations>
  newLifekey: A new life key
  newLifekey_no: En ny livsnøkkel
  newBelt: Yay, you're a {beltName}!
  newBelt_no: Yay, du er nå {beltName}!
  belt: 'belt'
  belt_no: 'belte'

  rankText: 'Your Rank: {rank}'
  rankText_no: 'Din ranking: {rank}'

  continueButton: Continue
  continueButton_no: Forstett
</translations>

<script>
import Belt from './Belt';
import Lifekey from './Lifekey';
import AnimatedSatellitesProgress from '@/components/AnimatedSatellitesProgress';
import StandardButton from '@/components/StandardButton';
import NextBeltProgressText from './NextBeltProgressText';

export default {
    components: { Belt, Lifekey, AnimatedSatellitesProgress, StandardButton, NextBeltProgressText },
    props: {
        belt: {
            type: Object,
            required: true
        },
        rank: {
            type: Number,
            default: 0 // Do not show rank
        },
        continueToDashboard: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showNewBelt: false
        };
    },
    computed: {
        beltProps() {
            const b = this.belt.points || this.showNewBelt ? this.belt.current : this.belt.prev;
            return {
                color: b ? b.color : '',
                avatar: this.$store && this.$store.getters['moduleAuth/avatar']
            };
        },
        progress() {
            if (this.belt.current && this.belt.points === 0) {
                return 1;
            } else if (this.belt.next) {
                return this.belt.points / this.belt.next.requiredPoints;
            } else {
                return 0;
            }
        },
        priorProgress() {
            if (this.belt.points && this.belt.next) {
                return (this.belt.points - 1) / this.belt.next.requiredPoints;
            } else if (!this.belt.points && this.belt.current) {
                return (this.belt.current.requiredPoints - 1) / this.belt.current.requiredPoints;
            } else {
                return 0;
            }
        },
        satelliteCount() {
            if (this.belt.next) {
                return this.belt.next.requiredPoints;
            } else {
                return 0;
            }
        },
        priorSatelliteCount() {
            if (this.belt.current && !this.belt.points) {
                return this.belt.current.requiredPoints;
            } else {
                return 0;
            }
        },
        currentBeltName() {
            return (
                this.$t(`belts.beltGrades.${this.belt.current.id}`) +
                ' ' +
                this.$t('belt') +
                (this.belt.current.number ? ` ${this.belt.current.number}` : '')
            );
        },
        nextBeltGrade() {
            return this.$t(`belts.beltGrades.${this.belt.next.id}`);
        }
    },
    methods: {
        navigateToDashboard() {
            this.$emit('signal');
            this.$router.push({
                name: 'DashboardHomeView'
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.LifekeyPopup {
    background-color: white;
    width: 30em;
}
.header {
    height: 20em;
    background-color: $dashboard-background;
    display: flex;
    justify-content: center;
    align-items: center;

    font-size: 150%;
}
.Belt {
    $size: 5em;
    width: $size;
    height: $size;
    position: absolute;
}

.Lifekey {
    $size: 3.5em;
    width: $size;
    height: $size;
}

.body {
    padding: 2em;
}
.title {
    font-weight: 600;
    font-size: 120%;
    color: rgba(black, 0.9);
    text-align: center;
}
.text {
    color: rgba(black, 0.7);
    margin-top: 0.3em;
    text-align: center;
}
.footer {
    border-top: 1px solid rgba(black, 0.05);
    padding: 1em;
    color: rgba(black, 0.7);
    text-align: center;
}

.continueButtonContainer {
    display: flex;
    justify-content: center;
    margin-top: 1em;
}

@include media('<mobile') {
    .header {
        font-size: 120%;
    }
}
</style>
